/**
 * Builds query string parameters with comma-separated values for arrays
 * instead of duplicate parameter names.
 *
 * @param {Object} params - Object with parameter names as keys
 * @returns {URLSearchParams} - URLSearchParams object with formatted parameters
 *
 * @example
 * buildQueryParams({
 *   page: 1,
 *   funnel: ['Insurance', 'Re-KYC'],
 *   name: 'John'
 * })
 * // Returns: ?page=1&funnel=Insurance,Re-KYC&name=John
 */
export const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      // Skip null/undefined values
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        // Join array values with commas
        queryParams.append(key, value.join(','));
      }
    } else {
      // Regular value
      queryParams.append(key, value.toString());
    }
  });

  return queryParams;
};

/**
 * Parses comma-separated query parameter values into arrays
 *
 * @param {URLSearchParams} searchParams - URLSearchParams object
 * @param {string} paramName - Name of the parameter to parse
 * @returns {string[]} - Array of values
 *
 * @example
 * parseArrayParam(searchParams, 'funnel')
 * // If URL is ?funnel=Insurance,Re-KYC
 * // Returns: ['Insurance', 'Re-KYC']
 */
export const parseArrayParam = (searchParams, paramName) => {
  const value = searchParams.get(paramName);
  if (!value) return [];

  return value.split(',').map(v => v.trim()).filter(v => v);
};

/**
 * Helper to add array parameter to existing URLSearchParams
 *
 * @param {URLSearchParams} queryParams - URLSearchParams object
 * @param {string} key - Parameter name
 * @param {any[]} values - Array of values
 */
export const appendArrayParam = (queryParams, key, values) => {
  if (values && values.length > 0) {
    queryParams.append(key, values.join(','));
  }
};
